// Use relative URL in development to leverage Create React App proxy
// In production, use full URL or set REACT_APP_API_BASE_URL
const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';

// Get JWT token from localStorage
const getToken = () => {
  return localStorage.getItem('portfolio_token');
};

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  try {
    const token = getToken();
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      headers,
      ...options,
    });
    
    if (!response.ok) {
      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401) {
        // Clear token and user data
        localStorage.removeItem('portfolio_token');
        localStorage.removeItem('portfolio_user');
        // Redirect to login would be handled by the app
        throw new Error('Session expired. Please login again.');
      }
      const errorData = await response.json().catch(() => ({ error: `HTTP error! status: ${response.status}` }));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('API Error:', error);
    
    // Provide more helpful error messages
    if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
      throw new Error(`Cannot connect to backend server at ${API_BASE}. Please ensure the backend is running on the correct port.`);
    }
    
    throw error;
  }
};

// Auth APIs
export const authApi = {
  login: (username, password) => 
    apiCall('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  register: (username, password) => 
    apiCall('/auth/register', { method: 'POST', body: JSON.stringify({ username, password }) }),
};

// Crypto APIs
export const cryptoApi = {
  getAll: () => apiCall('/crypto'),
  add: (data) => apiCall('/crypto', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/crypto/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/crypto/${id}`, { method: 'DELETE' }),
};

// Fixed Deposits APIs
export const fixedDepositsApi = {
  getAll: () => apiCall('/fixed-deposits'),
  add: (data) => apiCall('/fixed-deposits', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/fixed-deposits/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/fixed-deposits/${id}`, { method: 'DELETE' }),
};

// Bank Balance APIs
export const bankBalanceApi = {
  getAll: () => apiCall('/bank-balance'),
  add: (data) => apiCall('/bank-balance', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/bank-balance/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/bank-balance/${id}`, { method: 'DELETE' }),
};

// Overseas APIs
export const overseasApi = {
  getAll: () => apiCall('/overseas'),
  add: (data) => apiCall('/overseas', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/overseas/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/overseas/${id}`, { method: 'DELETE' }),
};

// Land APIs
export const landApi = {
  getAll: () => apiCall('/land'),
  add: (data) => apiCall('/land', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/land/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/land/${id}`, { method: 'DELETE' }),
};

// Insurance APIs
export const insuranceApi = {
  getAll: () => apiCall('/insurance'),
  add: (data) => apiCall('/insurance', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/insurance/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/insurance/${id}`, { method: 'DELETE' }),
};

// Stocks APIs
export const stocksApi = {
  getAll: () => apiCall('/stocks'),
  add: (data) => apiCall('/stocks', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/stocks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/stocks/${id}`, { method: 'DELETE' }),
};

// Money Lent APIs
export const moneyLentApi = {
  getAll: () => apiCall('/money-lent'),
  add: (data) => apiCall('/money-lent', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/money-lent/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/money-lent/${id}`, { method: 'DELETE' }),
};

// Dashboard APIs
export const dashboardApi = {
  getSummary: () => apiCall('/dashboard'),
  getWealthHistory: () => apiCall('/wealth-history'),
  recordWealth: (data) => apiCall('/wealth-history', { method: 'POST', body: JSON.stringify(data) }),
};
